import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { NewVersionComponent } from '@app/core/components/new-version/new-version.component'
import { concat, first, interval } from 'rxjs';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {

  readonly POLL_NEW_VERSION_INTERVAL: number = environment.pollNewVersionIntervalMinutes * 60 * 1000

  #appRef = inject(ApplicationRef);
  #swUpdate = inject(SwUpdate);
  #toastr = inject(ToastrService);

  private versionToastRef?: ActiveToast<any>;

  public start() {

    if (!this.#swUpdate.isEnabled) {
      this.removeServiceWorkers()
      return
    }

    this.log('START', `Waiting and polling for new versions.`)

    this.listenForUpdate()
    this.pollForUpdate()
  }

  private listenForUpdate(): void {

    this.#swUpdate.versionUpdates.subscribe((event) => {
      switch (event.type) {
        case 'NO_NEW_VERSION_DETECTED':
          break;

        case 'VERSION_DETECTED':
          this.log(event.type, `New version is detected: ${event.version.hash}.`);
          break;

        case 'VERSION_READY':
          this.log(event.type, `New version '${event.latestVersion.hash}' will replace current version '${event.currentVersion.hash}'.`)
          this.showNewAppDialog()
          break;

        case 'VERSION_INSTALLATION_FAILED':
          this.log(event.type, `Failed to install new version: ${event.error}`);
          break;
      }
    })
  }

  private pollForUpdate(): void {

    const isStable$ = this.#appRef.isStable.pipe(first((isStable) => isStable === true))
    const pollInterval$ = interval(this.POLL_NEW_VERSION_INTERVAL)

    concat(isStable$, pollInterval$).subscribe(async () => {
      try {
        const updateFound = await this.#swUpdate.checkForUpdate()
        this.log('CHECK_FOR_UPDATE', updateFound ? 'New version available' : 'No new version found');
      } catch (err) {
        console.error(`[AppUpdate] - Failed to check for updates:`, err);
      }
    })
  }

  private log(event: string, message?: string): void {
    console.info(`[AppUpdate] ${event.toUpperCase()}` + (message ? ` - ${message}` : ''))
  }

  private showNewAppDialog(): void {

    if (this.versionToastRef) return

    this.versionToastRef = this.#toastr.show('', '', {
      toastComponent: NewVersionComponent,
      positionClass: 'toast-bottom-left',
      timeOut: 0,
      tapToDismiss: false,
      closeButton: false,
    });

    if (this.versionToastRef?.toastRef?.componentInstance) {

      const instance = this.versionToastRef.toastRef.componentInstance as NewVersionComponent

      // Method for NewVersionComponent to call.
      instance.onReload = () => { window.location.reload() }
    }
  }

  private removeServiceWorkers(): void {

    if (!('serviceWorker' in navigator)) return

    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (const registration of registrations) {
        this.log('REMOVE', `Unregistering service worker: '${registration.scope}'.`)
        registration.unregister()
      }
    })
  }

}
